<template>
  <transition name="fade">
    <!-- OVERLAY: Fixed to viewport, blurred background -->
    <div v-if="show" 
         class="fixed top-0 left-0 w-screen h-screen flex items-center justify-center p-4"
         style="z-index: 999999; background-color: rgba(15, 23, 42, 0.7); backdrop-filter: blur(5px);">
      
      <!-- CARD CONTAINER: Single gradient background, rounded corners -->
      <div class="card-container">
        
        <!-- LOADING STATE -->
        <div v-if="isLoading" class="loading-state-container">
           <svg class="animate-spin h-10 w-10 mb-4" style="color: #7367F0;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm font-medium m-0" style="color: #7367F0;">Fetching Role Details...</p>
        </div>

        <!-- ERROR STATE -->
        <div v-else-if="error" class="loading-state-container" style="color: #ef4444;">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="m-0 font-medium">{{ error }}</p>
        </div>
        
        <!-- CONTENT WRAPPER (Scrollable) -->
        <div v-else class="content-wrapper">
          <!-- HEADER: Title on left, Close on right -->
          <div class="card-header">
            <div class="title-section">
              <h2 class="role-title">{{ roleData.title }}</h2>
              <p class="role-subtitle">Role Profile Breakdown</p>
            </div>
            <button @click="closeModal" class="close-button">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- BODY: Description and Skills -->
          <div class="card-body">
            <!-- Role Description -->
            <div class="info-section" v-if="parsedDescription">
              <h5 class="section-label">Role Description</h5>
              <p class="objective-text">
                {{ parsedDescription }}
              </p>
            </div>

            <!-- Functional Skills -->
            <div class="info-section" v-if="roleData.skills && roleData.skills.length">
              <h5 class="section-label">Functional Skills</h5>
              <div class="skills-container">
                <span v-for="(skill, index) in roleData.skills" :key="'fs-'+index" class="skill-chip">
                  {{ skill }}
                </span>
              </div>
            </div>

            <!-- Soft Skills -->
            <div class="info-section" v-if="cleanedSoftSkills && cleanedSoftSkills.length">
              <h5 class="section-label">Behavioural / Soft Skills</h5>
              <div class="skills-container">
                <span v-for="(skill, index) in cleanedSoftSkills" :key="'ss-'+index" class="skill-chip">
                  {{ skill }}
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "RoleCardModal",
  props: {
    show: {
      type: Boolean,
      required: true
    },
    roleId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isLoading: false,
      roleData: {},
      error: null
    };
  },
  computed: {
    parsedDescription() {
      if (!this.roleData || !this.roleData.description || !this.roleData.description.length) return "";
      let desc = this.roleData.description[0].msg || "";
      return desc.replace(/\[.*?\]\s*/g, '').trim();
    },
    cleanedSoftSkills() {
      if (!this.roleData || !this.roleData.softSkills) return [];
      return this.roleData.softSkills.filter(skill => !skill.startsWith('['));
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.roleId) {
        this.fetchRoleData();
      } else {
        setTimeout(() => {
            this.roleData = {};
            this.error = null;
        }, 300);
      }
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    async fetchRoleData() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await fetch(`https://api.getboarded.tech/kvdr-role?selfId=${this.roleId}`);
        const data = await response.json();
        
        if (data && data.data && data.data.length > 0) {
          this.roleData = data.data[0];
        } else {
          this.error = "No details found for this role.";
        }
      } catch (err) {
        this.error = "Failed to fetch role data. Please try again.";
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Smooth Modal transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Base Card Container */
.card-container {
  width: 100%;
  max-width: 400px;
  max-height: 85vh;
  border-radius: 24px;
  background: linear-gradient(180deg, #8C6DFD 0%, #6A4FE0 100%);
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  color: white;
}

/* Scrollable Inner Wrapper */
.content-wrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  scrollbar-width: none;      
  -ms-overflow-style: none;  
}
.content-wrapper::-webkit-scrollbar {
  display: none;
}

/* New Header on Gradient */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 16px 24px;
  flex-shrink: 0;
}

.title-section {
  padding-right: 16px;
}

.role-title {
  font-size: 1.5rem; /* 24px */
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  color: white;
}

.role-subtitle {
  font-size: 0.8125rem; /* 13px */
  margin: 4px 0 0 0;
  color: rgba(255, 255, 255, 0.8);
}

.close-button {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  padding: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;
  color: white;
  flex-shrink: 0;
}
.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Card Body (content area) */
.card-body {
  padding: 8px 24px 32px 24px;
  text-align: left;
}

.info-section {
  margin-bottom: 28px;
}
.info-section:last-child {
  margin-bottom: 0;
}

.section-label {
  font-size: 0.625rem; /* 10px */
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.75);
  margin-bottom: 12px;
}

.objective-text {
  font-size: 0.8125rem; /* 13px */
  line-height: 1.6;
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-chip {
  background-color: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 0.71rem; /* 11.5px */
  font-weight: 500;
  text-transform: capitalize;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

/* Centered Loading & Error Display */
.loading-state-container {
  padding: 60px;
  min-height: 350px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: white; /* Give it a solid background */
  width: 100%;
  height: 100%;
}
</style>